'use client';

import { createContext, createElement, useContext, type ReactNode } from 'react';

export type FeatureFlagSource = 'global' | 'solution' | 'template' | 'tenant';

export type ResolvedFeatureFlag = {
  key: string;
  enabled: boolean;
  source: FeatureFlagSource;
  updatedAt: number;
  payload?: unknown;
};

export type ResolvedFeatureFlags = Record<string, ResolvedFeatureFlag>;

const FeatureFlagsContext = createContext<ResolvedFeatureFlags | null>(null);

export function FeatureFlagsProvider({
  flags,
  children,
}: {
  flags: ResolvedFeatureFlags | null | undefined;
  children: ReactNode;
}) {
  return createElement(FeatureFlagsContext.Provider, { value: flags ?? null }, children);
}

export function useFeatureFlags(): ResolvedFeatureFlags | null {
  return useContext(FeatureFlagsContext);
}

export function isFeatureEnabled(
  flags: ResolvedFeatureFlags | null | undefined,
  key: string,
  fallback = false,
): boolean {
  return flags?.[key]?.enabled ?? fallback;
}

export function useFeatureFlag(key: string, fallback = false): boolean {
  return isFeatureEnabled(useFeatureFlags(), key, fallback);
}
